import { lazy, Suspense, type ReactNode } from 'react'
import { compileUiBlockSource, uiBlockSpecToMarkdown } from '@hub/contracts'
import { Skeleton } from '@/components/ui/skeleton'
import { useUiBlockHost } from './ui-block-host'

/**
 * The only way `message-markdown.tsx` reaches blocks: nothing of json-render,
 * zod or the table code loads until a spec fence or (flag on) a table is on
 * screen. A chunk that fails to load (a deploy replaced it) must not take the
 * message down, so both fall back to what the reply says without them.
 */

function UiBlockUnavailable({ source }: { source: string }) {
  const { renderMarkdown } = useUiBlockHost()
  return renderMarkdown(uiBlockSpecToMarkdown(compileUiBlockSource(source, true).spec))
}

const LazyUiBlock = lazy(() =>
  import('./ui-block').then(
    (module) => ({ default: module.UiBlock }),
    () => ({ default: UiBlockUnavailable })
  )
)

const LazyMarkdownTable = lazy(() =>
  import('./markdown-table').then(
    (module) => ({ default: module.MarkdownTable }),
    () => ({ default: ({ fallback }: { fallback: ReactNode }) => <>{fallback}</> })
  )
)

export function UiBlock({ source }: { source: string }) {
  return (
    <Suspense fallback={<Skeleton className="my-2 h-24 w-full rounded-lg" />}>
      <LazyUiBlock source={source} />
    </Suspense>
  )
}

export function MarkdownTable({
  children,
  fallback,
}: {
  children: ReactNode
  fallback: ReactNode
}) {
  return (
    <Suspense fallback={fallback}>
      <LazyMarkdownTable fallback={fallback}>{children}</LazyMarkdownTable>
    </Suspense>
  )
}
